import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Award, Gift, Loader, AlertCircle, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getTenantId } from '../../config/tenantConfig';
import { getValidAuthSession } from '../../utils/authStorage';
import apiCache from '../../utils/apiCache';
import './index.css';
import '../ProfileSectionPage/index.css';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://restomate.live/api';
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

// Routed page for the "Loyalty Points" section — follows the same
// isOpen/onClose/userId contract as MyOrdersModal/MyTransactionsModal/etc.
// so it plugs into App.js's <ProfileSectionPage SectionComponent={...}/> route
// wrapper unchanged. Reads the cached /web/me profile first (same cache
// Header/UserProfileModal already populate) and only hits the network if
// nothing is cached yet — no new endpoint, no duplicate integration.
export const LoyaltyModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [loyalty, setLoyalty] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchLoyalty = useCallback(async () => {
    const cachedProfile = apiCache.getUserProfile();
    if (cachedProfile) {
      setLoyalty(cachedProfile.loyalty || null);
      return;
    }

    const token = getValidAuthSession()?.token;
    if (!token) {
      setError('Please login to view your loyalty points');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/web/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Api-Key': X_API_KEY,
          'X-Tenant': getTenantId(),
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.data && response.data.data) {
        apiCache.setUserProfile(response.data.data);
        setLoyalty(response.data.data.loyalty || null);
      }
    } catch (err) {
      console.error('Error fetching loyalty data:', err);
      setError(err.response?.data?.message || 'Failed to load loyalty points. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchLoyalty();
    }
  }, [isOpen, fetchLoyalty]);

  if (!isOpen) return null;

  const points = loyalty?.points || {};
  const tier = loyalty?.tier || {};
  const progress = tier?.progress || {};
  const progressPct = Math.min(100, Math.max(0, Number(progress.progress_percentage) || 0));

  return (
    <>
      <div className="profile-section-header">
        <div className="profile-section-header-inner">
          <button
            onClick={() => navigate(-1)}
            style={{ background: 'none', border: 'none', padding: 0, marginRight: '15px', display: 'flex', alignItems: 'center', cursor: 'pointer', color: 'var(--color-primary, #ff6b35)' }}
            title="Go Back"
          >
            <ArrowLeft size={28} />
          </button>
          <Award size={28} />
          <h2>Loyalty Points</h2>
        </div>
      </div>

      <div className="profile-section-content">
        {loading ? (
          <div className="loyalty-state-container">
            <Loader size={40} className="loyalty-spin" />
            <p>Loading your loyalty points...</p>
          </div>
        ) : error ? (
          <div className="loyalty-state-container">
            <AlertCircle size={48} color="#dc3545" />
            <p style={{ color: '#dc3545' }}>{error}</p>
            <button className="apply-filters-btn" onClick={fetchLoyalty}>Try Again</button>
          </div>
        ) : !loyalty ? (
          <div className="loyalty-state-container empty">
            <Gift size={64} className="loyalty-empty-icon" />
            <h3>No Loyalty Points Yet</h3>
            <p>Place an order to start earning loyalty points.</p>
          </div>
        ) : (
          <>
            <div className="loyalty-hero-card">
              <div className="loyalty-hero-tier">
                <Star size={16} />
                <span>{tier.display || 'Bronze'} Tier</span>
              </div>
              <div className="loyalty-hero-points">
                {points.available ?? 0}
                <span className="loyalty-hero-points-label">available points</span>
              </div>

              {progress.next_tier && (
                <div className="loyalty-progress">
                  <div className="loyalty-progress-track">
                    <div className="loyalty-progress-fill" style={{ width: `${progressPct}%` }} />
                  </div>
                  <div className="loyalty-progress-labels">
                    <span>{progressPct}% to {progress.next_tier}</span>
                    {Number(progress.points_to_next_tier) > 0 && (
                      <span>{progress.points_to_next_tier} pts to go</span>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="loyalty-stats-grid">
              <div className="loyalty-stat-card">
                <span className="loyalty-stat-label">Available</span>
                <span className="loyalty-stat-value">{points.available ?? 0}</span>
              </div>
              <div className="loyalty-stat-card">
                <span className="loyalty-stat-label">Lifetime</span>
                <span className="loyalty-stat-value">{points.lifetime ?? 0}</span>
              </div>
              <div className="loyalty-stat-card">
                <span className="loyalty-stat-label">Redeemed</span>
                <span className="loyalty-stat-value">{points.redeemed ?? 0}</span>
              </div>
            </div>

            <p className="loyalty-footnote">
              Earn points on every order and redeem them for discounts. Points you've redeemed and earned per order also show up in Order Details under My Orders.
            </p>
          </>
        )}
      </div>
    </>
  );
};

export default LoyaltyModal;
